<template>
  <div>
    <div style="float: right; margin-bottom: 1rem;">
      <md-button class="md-success" @click="OpenCreateQRCodeModal()">
        <i class="ti ti-qrcode" aria-hidden="true"></i> Create QR Code
      </md-button>
    </div>

    <!-- Create QR Code Form -->
    <md-card v-if="showQrCodeForm" style="margin-bottom: 1.5rem;">
      <md-card-content>

        <!-- Step 1: Basic Settings -->
        <div class="step-header">
          <div class="step-badge">1</div>
          <span class="step-title">Basic settings</span>
        </div>
        <div class="form-section">
          <div class="grid-2">
            <div class="form-field">
              <label>Configuration name <span class="badge-required">required</span></label>
              <md-input v-model="configuration_name" type="text" placeholder="e.g. Office devices - London"></md-input>
            </div>
            <div class="toggle-row" style="margin-top: 22px;">
              <div class="toggle-info">
                <span class="toggle-label">Enable system apps</span>
                <span class="toggle-desc">Allow pre-installed system apps on enrollment</span>
              </div>
              <md-switch v-model="enable_system_apps" style="margin-left: 12px;"></md-switch>
            </div>
          </div>
          <div class="grid-2" style="margin-top: 12px;">
            <div class="form-field">
              <label>Default language</label>
              <select id="lang_dropdown" v-model="default_language" class="form-select">
                <option v-for="lang in languages" v-bind:key="lang.code" :value="lang.code">
                  {{ lang.code + ' - ' + lang.name }}
                </option>
              </select>
            </div>
            <div class="form-field">
              <label>Default timezone</label>
              <select id="timezone_dropdown" v-model="default_timezone" class="form-select">
                <option v-for="zone in timezone" v-bind:key="zone.value" :value="zone.value">
                  {{ zone.label + ' - ' + zone.value }}
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Step 2: WiFi Settings -->
        <div class="step-header" style="margin-top: 1.5rem;">
          <div class="step-badge">2</div>
          <span class="step-title">WiFi settings <span class="badge-optional">optional</span></span>
        </div>
        <div class="form-section">
          <div class="grid-3">
            <div class="form-field">
              <label>SSID name</label>
              <md-input type="text" v-model="wifi_ssid" placeholder="Office-WiFi"></md-input>
            </div>
            <div class="form-field">
              <label>Security type</label>
              <select id="wifi_security" v-model="wifi_security" class="form-select">
                <option value="None">None</option>
                <option value="WPA">WPA</option>
                <option value="WEP-PSK">WEP-PSK</option>
                <option value="WPA-PSK">WPA-PSK</option>
              </select>
            </div>
            <div class="form-field">
              <label>Passphrase</label>
              <md-input type="password" v-model="wifi_passphrase" placeholder="••••••••" :disabled="wifi_security === 'None'"></md-input>
            </div>
          </div>
        </div>

        <!-- Step 3: Google Account Settings -->
        <div class="step-header" style="margin-top: 1.5rem;">
          <div class="step-badge">3</div>
          <span class="step-title">Google account settings <span class="badge-optional">optional</span></span>
        </div>
        <div class="form-section">
          <div class="toggle-row">
            <div class="toggle-info">
              <span class="toggle-label">Require specific Google account</span>
              <span class="toggle-desc">Only the specified account can enroll with this QR code</span>
            </div>
            <md-switch v-model="require_specific_account" style="margin-left: 12px;"></md-switch>
          </div>
          <div v-if="require_specific_account" style="margin-top: 12px;">
            <div class="form-field">
              <label>Required Google account email</label>
              <md-input type="email" v-model="required_account_email" placeholder="employee@company.com"></md-input>
              <span class="field-hint">The user must sign in with this exact account during enrollment.</span>
            </div>
            <div class="info-box" style="margin-top: 10px;">
              <i class="ti ti-info-circle" style="font-size: 16px; color: var(--text-accent); flex-shrink: 0; margin-top: 1px;" aria-hidden="true"></i>
              <p>This requires a Google Workspace or Cloud Identity account. Personal Gmail accounts are not supported.</p>
            </div>
          </div>
        </div>

        <!-- Footer Actions -->
        <div class="form-footer">
          <md-button class="md-danger md-simple" @click="CloseForm()">
            <i class="ti ti-x" aria-hidden="true"></i> Cancel
          </md-button>
          <md-button class="md-success" :disabled="isDisabled" @click="CreateQrCode()">
            <i class="ti ti-qrcode" aria-hidden="true"></i>
            Generate QR code
            <span v-if="isDisabled"><i class="fa fa-spinner fa-spin"></i></span>
          </md-button>
        </div>

      </md-card-content>
    </md-card>

    <!-- QR Code List -->
    <md-card>
      <md-card-content>
        <md-table v-model="qrCodeData" table-header-color="green">
          <md-table-row slot="md-table-row" slot-scope="{ item }">
            <md-table-cell md-label="Name">{{ item.configuration_name }}</md-table-cell>
            <md-table-cell md-label="Language">{{ item.default_language || '-' }}</md-table-cell>
            <md-table-cell md-label="Timezone">{{ item.default_timezone || '-' }}</md-table-cell>
            <md-table-cell md-label="WiFi SSID">{{ item.wifi_ssid || '-' }}</md-table-cell>
            <md-table-cell md-label="Required Account">
              <span v-if="item.required_account_email" style="font-size: 12px; background: var(--bg-accent); color: var(--text-accent); padding: 2px 8px; border-radius: 4px;">
                {{ item.required_account_email }}
              </span>
              <span v-else style="color: var(--text-muted);">-</span>
            </md-table-cell>
            <md-table-cell md-label="Created on">{{ HumanReadableDateFormat(item.created_at) }}</md-table-cell>
            <md-table-cell md-label="Actions">
              <md-button class="md-just-icon md-success md-simple">
                <a @click="OpenShowQrCodeModal(item.qr_code_url, item.enrollment_token_response, item.profile_id.profile_type, item.byod_qr_code_url, item.current_qr_code, item._id)" href="javascript:void(0)">
                  <md-icon>qr_code</md-icon>
                </a>
              </md-button>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </md-card-content>
    </md-card>

    <!-- QR Code Modal -->
    <template>
      <modal v-if="ShowQrCodeModal" @close="ShowQrCodeModal = false">
        <template slot="header">
          <h4 class="modal-title">QR code</h4>
          <md-button style="height: 25px" class="md-simple md-just-icon md-round modal-default-button" @click="ShowQrCodeModal = false">
            <md-icon>clear</md-icon>
          </md-button>
        </template>
        <template slot="body">
          <div class="box-body">
            <div class="md-layout">
              <div class="md-layout-item">
                <md-switch v-model="show_byod_qr_code" @change="toggleQrCode()">Show BYOD QR code</md-switch>
                <div class="md-layout-item md-size-100" v-if="enterprise_token && show_byod_qr_code">
                  <span style="font-size: 11px; font-weight: 500;">Install using link:
                    <p class="text-primary">{{ enterprise_token }}
                      <a href="javascript:void(0)" style="color: green;" @click="CopyProfileContent(enterprise_token)">
                        <i class="fa fa-clone" aria-hidden="true"></i>
                      </a>
                    </p>
                  </span>
                </div>
              </div>
              <div class="md-layout-item md-size-100">
                <div class="android-img">
                  <img :src="barcode" style="width: unset;" alt="QR code image" /><br>
                  <p class="text-center">
                    <a href="javascript:void(0)" @click="downloadQrCode()">
                      <i class="fa fa-download" aria-hidden="true"></i>
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>
        <template slot="footer">
          <md-button class="md-danger md-simple" @click="ShowQrCodeModal = false">Close</md-button>
        </template>
      </modal>
    </template>
  </div>
</template>

<script>
import QrCodeDataService from "../../services/QrCodeDataService";
import { Modal } from "@/components";

export default {
  name: 'CreateQRCode',
  components: { Modal },
  props: {
    profileId: String,
  },
  data() {
    return {
      isDisabled: false,
      showQrCodeForm: false,
      configuration_name: null,
      enable_system_apps: false,
      default_timezone: null,
      default_language: null,
      wifi_ssid: "",
      wifi_security: "None",
      wifi_passphrase: "",
      require_specific_account: false,
      required_account_email: "",
      timezone: [
        {"label":"(GMT-12:00) International Date Line West","value":"Etc/GMT+12"},
        {"label":"(GMT-11:00) Midway Island, Samoa","value":"Pacific/Midway"},
        {"label":"(GMT-10:00) Hawaii","value":"Pacific/Honolulu"},
        {"label":"(GMT-09:00) Alaska","value":"US/Alaska"},
        {"label":"(GMT-08:00) Pacific Time (US & Canada)","value":"America/Los_Angeles"},
        {"label":"(GMT-08:00) Tijuana, Baja California","value":"America/Tijuana"},
        {"label":"(GMT-07:00) Arizona","value":"US/Arizona"},
        {"label":"(GMT-07:00) Chihuahua, La Paz, Mazatlan","value":"America/Chihuahua"},
        {"label":"(GMT-07:00) Mountain Time (US & Canada)","value":"US/Mountain"},
        {"label":"(GMT-06:00) Central America","value":"America/Managua"},
        {"label":"(GMT-06:00) Central Time (US & Canada)","value":"US/Central"},
        {"label":"(GMT-06:00) Guadalajara, Mexico City, Monterrey","value":"America/Mexico_City"},
        {"label":"(GMT-06:00) Saskatchewan","value":"Canada/Saskatchewan"},
        {"label":"(GMT-05:00) Bogota, Lima, Quito, Rio Branco","value":"America/Bogota"},
        {"label":"(GMT-05:00) Eastern Time (US & Canada)","value":"US/Eastern"},
        {"label":"(GMT-05:00) Indiana (East)","value":"US/East-Indiana"},
        {"label":"(GMT-04:00) Atlantic Time (Canada)","value":"Canada/Atlantic"},
        {"label":"(GMT-04:00) Caracas, La Paz","value":"America/Caracas"},
        {"label":"(GMT-04:00) Manaus","value":"America/Manaus"},
        {"label":"(GMT-04:00) Santiago","value":"America/Santiago"},
        {"label":"(GMT-03:30) Newfoundland","value":"Canada/Newfoundland"},
        {"label":"(GMT-03:00) Brasilia","value":"America/Sao_Paulo"},
        {"label":"(GMT-03:00) Buenos Aires, Georgetown","value":"America/Argentina/Buenos_Aires"},
        {"label":"(GMT-03:00) Greenland","value":"America/Godthab"},
        {"label":"(GMT-03:00) Montevideo","value":"America/Montevideo"},
        {"label":"(GMT-02:00) Mid-Atlantic","value":"America/Noronha"},
        {"label":"(GMT-01:00) Cape Verde Is.","value":"Atlantic/Cape_Verde"},
        {"label":"(GMT-01:00) Azores","value":"Atlantic/Azores"},
        {"label":"(GMT+00:00) Casablanca, Monrovia, Reykjavik","value":"Africa/Casablanca"},
        {"label":"(GMT+00:00) Greenwich Mean Time : Dublin, Edinburgh, Lisbon, London","value":"Etc/Greenwich"},
        {"label":"(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna","value":"Europe/Amsterdam"},
        {"label":"(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague","value":"Europe/Belgrade"},
        {"label":"(GMT+01:00) Brussels, Copenhagen, Madrid, Paris","value":"Europe/Brussels"},
        {"label":"(GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb","value":"Europe/Sarajevo"},
        {"label":"(GMT+01:00) West Central Africa","value":"Africa/Lagos"},
        {"label":"(GMT+02:00) Amman","value":"Asia/Amman"},
        {"label":"(GMT+02:00) Athens, Bucharest, Istanbul","value":"Europe/Athens"},
        {"label":"(GMT+02:00) Beirut","value":"Asia/Beirut"},
        {"label":"(GMT+02:00) Cairo","value":"Africa/Cairo"},
        {"label":"(GMT+02:00) Harare, Pretoria","value":"Africa/Harare"},
        {"label":"(GMT+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius","value":"Europe/Helsinki"},
        {"label":"(GMT+02:00) Jerusalem","value":"Asia/Jerusalem"},
        {"label":"(GMT+02:00) Minsk","value":"Europe/Minsk"},
        {"label":"(GMT+02:00) Windhoek","value":"Africa/Windhoek"},
        {"label":"(GMT+03:00) Kuwait, Riyadh, Baghdad","value":"Asia/Kuwait"},
        {"label":"(GMT+03:00) Moscow, St. Petersburg, Volgograd","value":"Europe/Moscow"},
        {"label":"(GMT+03:00) Nairobi","value":"Africa/Nairobi"},
        {"label":"(GMT+03:00) Tbilisi","value":"Asia/Tbilisi"},
        {"label":"(GMT+03:30) Tehran","value":"Asia/Tehran"},
        {"label":"(GMT+04:00) Abu Dhabi, Muscat","value":"Asia/Muscat"},
        {"label":"(GMT+04:00) Baku","value":"Asia/Baku"},
        {"label":"(GMT+04:00) Yerevan","value":"Asia/Yerevan"},
        {"label":"(GMT+04:30) Kabul","value":"Asia/Kabul"},
        {"label":"(GMT+05:00) Yekaterinburg","value":"Asia/Yekaterinburg"},
        {"label":"(GMT+05:00) Islamabad, Karachi, Tashkent","value":"Asia/Karachi"},
        {"label":"(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi","value":"Asia/Calcutta"},
        {"label":"(GMT+05:45) Kathmandu","value":"Asia/Katmandu"},
        {"label":"(GMT+06:00) Almaty, Novosibirsk","value":"Asia/Almaty"},
        {"label":"(GMT+06:00) Astana, Dhaka","value":"Asia/Dhaka"},
        {"label":"(GMT+06:30) Yangon (Rangoon)","value":"Asia/Rangoon"},
        {"label":"(GMT+07:00) Bangkok, Hanoi, Jakarta","value":"Asia/Bangkok"},
        {"label":"(GMT+07:00) Krasnoyarsk","value":"Asia/Krasnoyarsk"},
        {"label":"(GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi","value":"Asia/Hong_Kong"},
        {"label":"(GMT+08:00) Kuala Lumpur, Singapore","value":"Asia/Kuala_Lumpur"},
        {"label":"(GMT+08:00) Irkutsk, Ulaan Bataar","value":"Asia/Irkutsk"},
        {"label":"(GMT+08:00) Perth","value":"Australia/Perth"},
        {"label":"(GMT+08:00) Taipei","value":"Asia/Taipei"},
        {"label":"(GMT+09:00) Osaka, Sapporo, Tokyo","value":"Asia/Tokyo"},
        {"label":"(GMT+09:00) Seoul","value":"Asia/Seoul"},
        {"label":"(GMT+09:00) Yakutsk","value":"Asia/Yakutsk"},
        {"label":"(GMT+09:30) Adelaide","value":"Australia/Adelaide"},
        {"label":"(GMT+09:30) Darwin","value":"Australia/Darwin"},
        {"label":"(GMT+10:00) Brisbane","value":"Australia/Brisbane"},
        {"label":"(GMT+10:00) Canberra, Melbourne, Sydney","value":"Australia/Canberra"},
        {"label":"(GMT+10:00) Hobart","value":"Australia/Hobart"},
        {"label":"(GMT+10:00) Guam, Port Moresby","value":"Pacific/Guam"},
        {"label":"(GMT+10:00) Vladivostok","value":"Asia/Vladivostok"},
        {"label":"(GMT+11:00) Magadan, Solomon Is., New Caledonia","value":"Asia/Magadan"},
        {"label":"(GMT+12:00) Auckland, Wellington","value":"Pacific/Auckland"},
        {"label":"(GMT+12:00) Fiji, Kamchatka, Marshall Is.","value":"Pacific/Fiji"},
        {"label":"(GMT+13:00) Nuku'alofa","value":"Pacific/Tongatapu"}
      ],
      languages: [
        { code: 'ab', name: 'Abkhazian' },{ code: 'aa', name: 'Afar' },{ code: 'af', name: 'Afrikaans' },
        { code: 'ak', name: 'Akan' },{ code: 'sq', name: 'Albanian' },{ code: 'am', name: 'Amharic' },
        { code: 'ar', name: 'Arabic' },{ code: 'an', name: 'Aragonese' },{ code: 'hy', name: 'Armenian' },
        { code: 'as', name: 'Assamese' },{ code: 'av', name: 'Avaric' },{ code: 'ae', name: 'Avestan' },
        { code: 'ay', name: 'Aymara' },{ code: 'az', name: 'Azerbaijani' },{ code: 'bm', name: 'Bambara' },
        { code: 'ba', name: 'Bashkir' },{ code: 'eu', name: 'Basque' },{ code: 'be', name: 'Belarusian' },
        { code: 'bn', name: 'Bengali' },{ code: 'bs', name: 'Bosnian' },{ code: 'br', name: 'Breton' },
        { code: 'bg', name: 'Bulgarian' },{ code: 'my', name: 'Burmese' },{ code: 'ca', name: 'Catalan' },
        { code: 'zh', name: 'Chinese' },{ code: 'hr', name: 'Croatian' },{ code: 'cs', name: 'Czech' },
        { code: 'da', name: 'Danish' },{ code: 'nl', name: 'Dutch' },{ code: 'en', name: 'English' },
        { code: 'eo', name: 'Esperanto' },{ code: 'et', name: 'Estonian' },{ code: 'fi', name: 'Finnish' },
        { code: 'fr', name: 'French' },{ code: 'gl', name: 'Galician' },{ code: 'ka', name: 'Georgian' },
        { code: 'de', name: 'German' },{ code: 'el', name: 'Greek' },{ code: 'gu', name: 'Gujarati' },
        { code: 'he', name: 'Hebrew' },{ code: 'hi', name: 'Hindi' },{ code: 'hu', name: 'Hungarian' },
        { code: 'is', name: 'Icelandic' },{ code: 'id', name: 'Indonesian' },{ code: 'ga', name: 'Irish' },
        { code: 'it', name: 'Italian' },{ code: 'ja', name: 'Japanese' },{ code: 'kn', name: 'Kannada' },
        { code: 'kk', name: 'Kazakh' },{ code: 'ko', name: 'Korean' },{ code: 'ku', name: 'Kurdish' },
        { code: 'lo', name: 'Lao' },{ code: 'la', name: 'Latin' },{ code: 'lv', name: 'Latvian' },
        { code: 'lt', name: 'Lithuanian' },{ code: 'mk', name: 'Macedonian' },{ code: 'ms', name: 'Malay' },
        { code: 'ml', name: 'Malayalam' },{ code: 'mt', name: 'Maltese' },{ code: 'mi', name: 'Maori' },
        { code: 'mr', name: 'Marathi' },{ code: 'mn', name: 'Mongolian' },{ code: 'ne', name: 'Nepali' },
        { code: 'no', name: 'Norwegian' },{ code: 'fa', name: 'Persian' },{ code: 'pl', name: 'Polish' },
        { code: 'pt', name: 'Portuguese' },{ code: 'pa', name: 'Punjabi' },{ code: 'ro', name: 'Romanian' },
        { code: 'ru', name: 'Russian' },{ code: 'sr', name: 'Serbian' },{ code: 'si', name: 'Sinhala' },
        { code: 'sk', name: 'Slovak' },{ code: 'sl', name: 'Slovenian' },{ code: 'so', name: 'Somali' },
        { code: 'es', name: 'Spanish' },{ code: 'sw', name: 'Swahili' },{ code: 'sv', name: 'Swedish' },
        { code: 'tl', name: 'Tagalog' },{ code: 'ta', name: 'Tamil' },{ code: 'te', name: 'Telugu' },
        { code: 'th', name: 'Thai' },{ code: 'tr', name: 'Turkish' },{ code: 'uk', name: 'Ukrainian' },
        { code: 'ur', name: 'Urdu' },{ code: 'uz', name: 'Uzbek' },{ code: 'vi', name: 'Vietnamese' },
        { code: 'cy', name: 'Welsh' },{ code: 'xh', name: 'Xhosa' },{ code: 'yi', name: 'Yiddish' },
        { code: 'yo', name: 'Yoruba' },{ code: 'zu', name: 'Zulu' }
      ],
      qrCodeData: [],
      barcode: "",
      byow_qr_code: "",
      normal_qr_code: "",
      show_byod_qr_code: false,
      enterprise_token: "",
      ShowQrCodeModal: false,
      currentProfileType: "",
      currentQrCodeID: "",
    }
  },
  methods: {
    OpenCreateQRCodeModal() {
        this.showQrCodeForm = true;
        setTimeout(() => {  // use arrow function to keep 'this' context
            $('#lang_dropdown').select2({ placeholder: "Select language", theme: "classic" });
            $('#timezone_dropdown').select2({ placeholder: "Select timezone", theme: "classic" });
            $('#wifi_security').select2({ placeholder: "Select security type", theme: "classic" });

            // ADD THIS - sync select2 change back to Vue
            $('#wifi_security').on('change', (e) => {
                this.wifi_security = e.target.value;
            });
        }, 300);
    },

    CloseForm() {
      this.showQrCodeForm = false;
      this.configuration_name = null;
      this.wifi_ssid = "";
      this.wifi_security = "None";
      this.wifi_passphrase = "";
      this.require_specific_account = false;
      this.required_account_email = "";
      this.enable_system_apps = false;
    },

    CreateQrCode() {
      if (!this.configuration_name) {
        this.$toast.error("Configuration name is required.");
        return;
      }
      if (this.require_specific_account && !this.required_account_email) {
        this.$toast.error("Please enter a required Google account email.");
        return;
      }
      this.isDisabled = true;
      let data = {
        configuration_name: this.configuration_name,
        profile_id: this.profileId,
        default_language: $('#lang_dropdown').val(),
        default_timezone: $('#timezone_dropdown').val(),
        enable_system_apps: this.enable_system_apps,
        wifi_ssid: this.wifi_ssid,
        wifi_security: $('#wifi_security').val(),
        wifi_passphrase: this.wifi_passphrase,
        required_account_email: this.require_specific_account ? this.required_account_email : null,
      };

      QrCodeDataService.CreateQrCode(data)
        .then((response) => {
          this.isDisabled = false;
          this.CloseForm();
          this.GetQrCodeList();
          this.$toast.success(response.data.respones_msg);
        })
        .catch((e) => {
          this.isDisabled = false;
          this.$toast.error(e.response.data.respones_msg);
        });
    },

    GetQrCodeList() {
      QrCodeDataService.GetQrCodeList({ profile_id: this.profileId })
        .then((response) => {
          if (response.data.data) this.qrCodeData = response.data.data;
        })
        .catch((e) => { this.$toast.error(e); });
    },

    OpenShowQrCodeModal(qrCode, enterpriseToken, profileType, byow_qr_code, current_qr_code, id) {
      this.currentQrCodeID = id;
      this.show_byod_qr_code = current_qr_code === "BYOD";
      this.barcode = this.show_byod_qr_code ? byow_qr_code : qrCode;
      this.byow_qr_code = byow_qr_code;
      this.normal_qr_code = qrCode;
      this.currentProfileType = profileType;
      this.enterprise_token = enterpriseToken
        ? (profileType === "Android" ? "https://enterprise.google.com/android/enroll?et=" + enterpriseToken.value : enterpriseToken.value)
        : "";
      this.ShowQrCodeModal = true;
    },

    toggleQrCode() {
      let old = this.barcode;
      this.barcode = this.show_byod_qr_code ? this.byow_qr_code : this.normal_qr_code;
      this.byow_qr_code = old;
      QrCodeDataService.UpdateQrCode({
        id: this.currentQrCodeID,
        current_qr_code: this.show_byod_qr_code ? "BYOD" : "Normal"
      }).catch((e) => { if (e.response) this.$toast.error(e.response.data.respones_msg); });
    },

    CopyProfileContent(content) {
      navigator.clipboard.writeText(content);
      this.$toast.success("Copied to clipboard.");
    },

    async downloadQrCode() {
      let base64 = this.show_byod_qr_code ? this.barcode : await this.getBase64FromUrl(this.barcode);
      let a = document.createElement("a");
      a.href = base64;
      a.download = "QRCode.png";
      a.click();
    },

    async getBase64FromUrl(url) {
      const data = await fetch(url);
      const blob = await data.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => resolve(reader.result);
      });
    }
  },
  beforeMount() {
    this.GetQrCodeList();
  },
}
</script>

<style scoped>
.step-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 1rem;
}
.step-badge {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #4caf50;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}
.step-title {
  font-size: 15px;
  font-weight: 500;
}
.form-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 0.5rem;
}
.grid-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.grid-3 {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
}
.form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.form-field label {
  font-size: 13px;
  color: #666;
  font-weight: 500;
}
.form-select {
  height: 36px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 0 8px;
  font-size: 14px;
  width: 100%;
}
.field-hint {
  font-size: 12px;
  color: #999;
}
.badge-required {
  font-size: 11px;
  background: #ffebee;
  color: #c62828;
  border-radius: 4px;
  padding: 2px 6px;
  margin-left: 4px;
  font-weight: 500;
}
.badge-optional {
  font-size: 11px;
  background: #fff8e1;
  color: #f57f17;
  border-radius: 4px;
  padding: 2px 6px;
  margin-left: 4px;
  font-weight: 500;
}
.toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.toggle-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.toggle-label {
  font-size: 14px;
}
.toggle-desc {
  font-size: 12px;
  color: #999;
}
.info-box {
  background: #e3f2fd;
  border: 1px solid #90caf9;
  border-radius: 4px;
  padding: 10px 12px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
.info-box p {
  font-size: 13px;
  color: #1565c0;
  line-height: 1.5;
  margin: 0;
}
.form-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
  margin-top: 1rem;
}
@media (max-width: 600px) {
  .grid-2, .grid-3 { grid-template-columns: 1fr; }
}
</style>